import React, { useState } from 'react';
import { Search, FileText } from 'lucide-react';
import api from '../../services/api';
import { apriFile } from '../../services/fileUrl';
import { messaggioDa } from '../../utils/notifiche';
import { TIPI_DOCUMENTO } from '../../utils/tipiDocumento';
import '../../styles/RicercaDocumenti.css';
import '../../styles/RicercaDocumentiConsole.css';

/**
 * RicercaDocumenti — «in quale documento di questo paziente si parla di X».
 *
 * Cerca nel TESTO dei documenti, anche dentro le scansioni (il testo lo
 * legge il riconoscimento in sottofondo, dopo il caricamento). E' una
 * ricerca per parole, con le radici italiane e senza badare agli
 * accenti: «trattamento» trova «trattamenti», «eta» trova «età». Fra
 * virgolette cerca la frase; -parola la esclude; OR fra due parole.
 *
 * I frammenti che tornano SONO contenuto clinico del documento: li
 * filtra il server con la stessa regola del documento intero, quindi qui
 * si mostra quello che arriva e basta. Chi non puo' aprire una procedura
 * riservata non ne trova i documenti, e il conteggio non li conta.
 *
 * Solo dentro il paziente, per scelta: una ricerca su tutti i pazienti e'
 * una funzione diversa e piu' delicata.
 */

/* Le etichette dei tipi: le stesse della linguetta Documenti, da un elenco
   solo (fino al 6/10/2026 erano due copie, e quella della linguetta non
   aveva «referto»). */
const etichettaTipo = (tipo) => TIPI_DOCUMENTO[tipo]?.testo || tipo;

const dataIta = (v) => (v ? new Date(v).toLocaleDateString('it-IT') : '');

/* Il frammento arriva con «» attorno alle parole trovate: si rendono in
   grassetto senza passare da HTML. */
const Frammento = ({ testo }) => {
    const pezzi = String(testo || '').split(/(«[^»]*»)/g);
    return (
        <span className="rd-frammento">
            {pezzi.map((p, i) => (p.startsWith('«') && p.endsWith('»')
                ? <mark key={i}>{p.slice(1, -1)}</mark>
                : <React.Fragment key={i}>{p}</React.Fragment>))}
        </span>
    );
};

const RicercaDocumenti = ({ pazienteId }) => {
    const [termine, setTermine] = useState('');
    const [esito, setEsito] = useState(null);   // { termine, risultati, totale, limite }
    const [errore, setErrore] = useState(null);
    const [inCorso, setInCorso] = useState(false);

    const cerca = async (e) => {
        if (e) e.preventDefault();
        const q = termine.trim();
        if (q.length < 2) { setErrore('Scrivi almeno due lettere da cercare.'); return; }
        setInCorso(true); setErrore(null);
        try {
            const r = await api.get(`/modulistica/paziente/${pazienteId}/ricerca`, { params: { q } });
            setEsito(r.data?.data || null);
        } catch (err) {
            setEsito(null);
            setErrore(messaggioDa(err, 'Non riesco a cercare nei documenti'));
        } finally {
            setInCorso(false);
        }
    };

    const azzera = () => { setTermine(''); setEsito(null); setErrore(null); };

    /* Due sorgenti: i documenti archiviati e i documenti caricati dentro
       le procedure (che stanno con le fotografie). L'indirizzo del file
       arriva dal server, gia' filtrato dalla riservatezza, e si apre con
       il token come dalla galleria. */
    const etichettaFonte = (r) => (r.fonte === 'procedura'
        ? `caricato nella procedura${r.data_visita ? ` del ${dataIta(r.data_visita)}` : ''}`
        : etichettaTipo(r.tipo));

    return (
        <section className="rd-riquadro">
            <form className="rd-modulo" onSubmit={cerca}>
                <Search size={16} strokeWidth={1.8} aria-hidden="true" />
                <input type="search" value={termine} onChange={(e) => setTermine(e.target.value)}
                       placeholder="Cerca una parola nei documenti, anche dentro le scansioni…"
                       aria-label="Cerca nei documenti del paziente" />
                <button type="submit" disabled={inCorso}>{inCorso ? 'Cerco…' : 'Cerca'}</button>
                {(esito || errore) && <button type="button" className="rd-azzera" onClick={azzera}>Chiudi</button>}
            </form>
            {errore && <p className="rd-errore">{errore}</p>}
            {esito && (
                <div className="rd-risultati">
                    <p className="rd-conteggio">
                        {esito.totale === 0
                            ? <>Nessun documento parla di «{esito.termine}».</>
                            : <>{esito.totale === 1 ? 'Un documento parla' : `${esito.totale} documenti parlano`} di «{esito.termine}»
                                {esito.limite ? ` (mostro i primi ${esito.limite})` : ''}.</>}
                    </p>
                    <ul className="rd-elenco">
                        {esito.risultati.map((r) => (
                            <li key={`${r.fonte}:${r.id}`} className="rd-voce">
                                <div className="rd-testa">
                                    <FileText size={14} strokeWidth={1.8} aria-hidden="true" />
                                    <span className="rd-tipo">{etichettaFonte(r)}</span>
                                    <span className="rd-titolo">{r.titolo || '—'}</span>
                                    <span className="rd-data">{dataIta(r.created_at)}</span>
                                    {r.metodo === 'ocr' && (
                                        <span className="rd-ocr" title="Testo letto da una scansione: può contenere errori di lettura">
                                            scansione{r.confidenza != null ? ` · ${r.confidenza}%` : ''}
                                        </span>
                                    )}
                                    {r.url && (
                                        <button type="button" className="rd-apri" onClick={() => apriFile(r.url)}>
                                            Apri
                                        </button>
                                    )}
                                </div>
                                <Frammento testo={r.frammento} />
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </section>
    );
};

export default RicercaDocumenti;
